/* eslint-disable arrow-body-style */
import {Actions, createEffect, ofType} from "@ngrx/effects";
import {concatLatestFrom} from "@ngrx/operators";
import {inject} from "@angular/core";
import {
    catchError, concatMap, EMPTY, exhaustMap, filter, finalize, forkJoin, from, interval, map, mergeMap,
    of, pairwise, scan, startWith, switchMap, takeUntil, takeWhile
} from "rxjs";
import {Store} from "@ngrx/store";
import {
    selectHasActiveMergeJobs,
    selectHasProcessingJobs,
    selectPendingScraperBatchJobIds,
    selectScraperBatchJobs,
    selectScraperEditLog,
    selectScraperJobId,
    selectScraperJobsParams
} from "./import.selectors";
import {AccelexImportActions, AccelexImportLogActions, DatasetActions, ForecastImportActions, ForecastReviewActions, ScraperActions} from "./import.actions";
import {ForecastImportService} from "../services/forecast-import.service";
import {INITIAL_MERGE_POLL_STATE, MERGE_POLL_INTERVAL_MS, MergePollEvent, reduceMergePoll} from "./forecast-merge-poll";
import {DealService} from "../../deal/services/deal.service";
import {FundApiService} from "../../fund/generated-sources/api";
import {AccelexImportService} from "../services/accelex-import.service";
import {ScraperService} from "../services/scraper.service";
import {DatasetService} from "../services/dataset.service";
import {MergeJobInfo} from "../models/merge-job-info";
import {MergeDataset} from "../models/merge-dataset";
import {DealListActions} from "../../deal/store/deal.actions";
import {NotificationActions} from "../../notification/store/notification.actions";
import {ImportInfo} from "../models/import-info";
import {Router} from "@angular/router";
import {ErrorHandlingUtil} from "../../shared/utils/error-handling-util";
import {HttpResponseError} from "../../shared/model/http-response-error";
import {MatSnackBar} from "@angular/material/snack-bar";
import {DownloadUtil} from "../../shared/utils/download-util";
import {take, tap} from "rxjs/operators";
import {AuthenticationService} from "../../shared/services/login/authentication.service";
import {ImportResult, ImportWarning} from "../models/import-result";
import {navigateToImportedFund} from "../utils/imported-fund-navigation";
import {summarizeBulkResults, toBulkResult} from "../../shared/utils/bulk-operation-util";
import {CapitalAccountReviewActions} from "./capital-account-review/capital-account-review.actions";
import {ScrapeExportRequest} from "../models/scrape-export";

/**
 * A capital-account-statement import, as opposed to a GP quarterly report: CAS approve stages
 * blocks for review and creates no fund, so it carries a dataset but no fundId.
 *
 * Both post-import navigations key off this one predicate. They used to disagree — the CAS effect
 * navigated to the review page while navigateAfterScraperImport unconditionally went to /import on
 * the same action, so which one won came down to effect subscription order (i.e. the bundler).
 */
const isCapitalAccountImport = (importResponse: ImportResult): boolean =>
    importResponse.fundId == null && !!importResponse.dataset;


export const loadImportFiles = createEffect(
    (actions$ = inject(Actions), accelexImportService = inject(AccelexImportService)) => {
        return actions$.pipe(
            ofType(AccelexImportActions.load),
            exhaustMap(() =>
                accelexImportService.getImportFiles().pipe(
                    map((fileNames) => AccelexImportActions.loaded({fileNames})),
                    catchError((errorResponse: any) => of(AccelexImportActions.loaderror({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                )
            )
        );
    },
    {functional: true}
);

export const loadDealAfterLoadingImportFiles = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(AccelexImportActions.loaded),
            map(() => DealListActions.load())
        );
    },
    {functional: true}
);

export const loadDatasets = createEffect(
    (actions$ = inject(Actions), datasetService = inject(DatasetService)) => {
        return actions$.pipe(
            ofType(DatasetActions.load),
            exhaustMap(() =>
                datasetService.getDatasets().pipe(
                    map((datasets) => DatasetActions.loaded({datasets})),
                    catchError((errorResponse: any) => of(DatasetActions.loaderror({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                )
            )
        );
    },
    {functional: true}
);

export const startImportJob = createEffect(
    (actions$ = inject(Actions), accelexImportService = inject(AccelexImportService)) => {
        return actions$.pipe(
            ofType(AccelexImportActions.import),
            exhaustMap((action) => {
                const importInfo: ImportInfo = {files: action.filesNames, dealId: action.dealId};
                return accelexImportService.startImportJob(importInfo).pipe(
                    map((taskInfoId) => AccelexImportActions.startpolling({taskInfoId})),
                    catchError((errorResponse: any) => of(AccelexImportActions.importJobError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                );
            })
        );
    },
    {functional: true}
);

export const pollingJobStatus = createEffect(
    (actions$ = inject(Actions), accelexImportService = inject(AccelexImportService)) => {
        return actions$.pipe(
            ofType(AccelexImportActions.startpolling),
            switchMap((action) =>
                interval(2000).pipe(
                    startWith(0),
                    switchMap(() =>
                        accelexImportService.getImportJobStatus(action.taskInfoId).pipe(
                            map((status) => {
                                if (status.status === "COMPLETED") {
                                    return AccelexImportActions.importSuccess({jobStatus: status});
                                } else if (["ERROR", "TIMEOUT", "CANCELLED"].includes(status.status)) {
                                    return AccelexImportActions.importError({jobStatus: status});
                                } else {
                                    return AccelexImportActions.polling();
                                }
                            }),
                            catchError((errorResponse: any) => of(AccelexImportActions.importJobError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                        )
                    ),
                    takeUntil(actions$.pipe(ofType(AccelexImportActions.importSuccess, AccelexImportActions.importError)))
                )
            )
        );
    },
    {functional: true}
);

export const loadDatasetsAfterImport = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(AccelexImportActions.importSuccess, AccelexImportActions.importError),
            map(() => DatasetActions.load())
        );
    },
    {functional: true}
);

export const mergeDatasets = createEffect(
    (actions$ = inject(Actions), datasetService = inject(DatasetService)) => {
        return actions$.pipe(
            ofType(DatasetActions.merge),
            exhaustMap((action) => {
                const mergeDataset: MergeDataset = {
                    sourceDataset: action.source,
                    targetDataset: action.target
                };
                return datasetService.mergeDatasets(mergeDataset).pipe(
                    map((taskInfoId) => DatasetActions.mergeStarted({taskInfoId, source: action.source, target: action.target})),
                    catchError((errorResponse: any) => {
                        const httpResponseError: HttpResponseError = {
                            statusCode: errorResponse.status,
                            errorMessage: ErrorHandlingUtil.getErrorMessage(errorResponse)
                        };
                        return of(DatasetActions.mergeError({httpResponseError}));
                    })
                );
            })
        );
    },
    {functional: true}
);

// When the user changes the merge target set (header dropdown), clear every persisted merge target in the
// source dataset (they reference the old target set), persist the newly chosen target, and only then switch
// the active target. Persisting the choice here — rather than reacting to the generic setsourceandtarget —
// keeps view/navigation dispatches (which also emit setsourceandtarget, defaulting to master) from silently
// overwriting a dataset's saved target. If either server call fails the target is left unchanged so no
// stale targets survive, and the failure is surfaced via resetMergeTargetsError.
export const resetMergeTargetsAndSetTarget = createEffect(
    (actions$ = inject(Actions), datasetService = inject(DatasetService)) => {
        return actions$.pipe(
            ofType(DatasetActions.resetMergeTargetsAndSetTarget),
            switchMap((action) =>
                forkJoin([
                    datasetService.clearMergeTargets(action.source),
                    datasetService.setMergeTarget({sourceDataset: action.source, targetDataset: action.target})
                ]).pipe(
                    map(() => DatasetActions.setsourceandtarget({source: action.source, target: action.target})),
                    catchError((errorResponse: any) =>
                        of(DatasetActions.resetMergeTargetsError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)}))
                    )
                )
            )
        );
    },
    {functional: true}
);

export const showResetMergeTargetsError = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(DatasetActions.resetMergeTargetsError),
            tap(({errorMsg}) => snackBar.open("Could not reset merge targets: " + errorMsg, "", {duration: 5000}))
        );
    },
    {functional: true, dispatch: false}
);

// Load the persisted merge-target assignments so the Manage Datasets target dropdowns can be seeded.
export const loadMergeTargets = createEffect(
    (actions$ = inject(Actions), datasetService = inject(DatasetService)) => {
        return actions$.pipe(
            ofType(DatasetActions.loadMergeTargets),
            exhaustMap(() =>
                datasetService.getMergeTargets().pipe(
                    map((mergeTargets) => DatasetActions.loadMergeTargetsSuccess({mergeTargets})),
                    catchError((errorResponse: any) =>
                        of(DatasetActions.loadMergeTargetsError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)}))
                    )
                )
            )
        );
    },
    {functional: true}
);

// When the user picks a target for a source dataset in the Manage Datasets table, persist the assignment
// and clear the now-stale merge targets already matched against the old target set. Both server calls must
// succeed; on failure surface an error so the user knows the change did not stick.
export const changeDatasetMergeTarget = createEffect(
    (actions$ = inject(Actions), datasetService = inject(DatasetService)) => {
        return actions$.pipe(
            ofType(DatasetActions.changeDatasetMergeTarget),
            // concatMap (not mergeMap): serialize target changes for a source so two rapid confirmations
            // can't run concurrently — with mergeMap the last setMergeTarget to resolve wins, which need
            // not be the last one the user picked, leaving the persisted target out of sync with the UI.
            // concatMap over exhaustMap so a second distinct change isn't dropped.
            concatMap((action) =>
                forkJoin([
                    datasetService.setMergeTarget({sourceDataset: action.source, targetDataset: action.target}),
                    datasetService.clearMergeTargets(action.source)
                ]).pipe(
                    switchMap(() => EMPTY),
                    catchError((errorResponse: any) =>
                        of(DatasetActions.changeDatasetMergeTargetError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)}))
                    )
                )
            )
        );
    },
    {functional: true}
);

export const showChangeDatasetMergeTargetError = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(DatasetActions.changeDatasetMergeTargetError),
            tap(({errorMsg}) => snackBar.open("Could not change merge target: " + errorMsg, "", {duration: 5000}))
        );
    },
    {functional: true, dispatch: false}
);

// Switch the notification bell's poll cadence to 5 s while a dataset merge is in flight.
// The matching JOB_FINISHED notification untracks itself when it arrives in the panel list.
export const trackMergeJob = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(DatasetActions.mergeStarted),
            map(({taskInfoId}) => NotificationActions.trackJob({taskInfoId}))
        );
    },
    {functional: true}
);

// Detects merge-job terminal transitions by comparing consecutive list loads.
// When a job that was IN_PROGRESS/NEW in the previous snapshot is no longer
// active in the current one, dispatches mergeSuccess / mergeError. This replaces
// the former per-job pollMergeStatus loop — the list is the single source of truth.
export const detectMergeTerminalTransitions = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(DatasetActions.loadMergeJobsSuccess),
            map(action => action.jobs),
            startWith([] as MergeJobInfo[]),
            pairwise(),
            mergeMap(([prev, curr]) => {
                const wasActive = new Set<number>(
                    prev
                        .filter(j => j.status === "IN_PROGRESS" || j.status === "NEW")
                        .map(j => j.taskInfoId)
                );
                return curr.filter(j =>
                    wasActive.has(j.taskInfoId) &&
                    j.status !== "IN_PROGRESS" &&
                    j.status !== "NEW"
                );
            }),
            map(job => job.status === "COMPLETED"
                ? DatasetActions.mergeSuccess({source: job.target, target: undefined})
                : DatasetActions.mergeError({
                    httpResponseError: {
                        statusCode: 500,
                        errorMessage: job.errorMessage ?? `Dataset merge ${job.status.toLowerCase()}`
                    }
                })
            )
        );
    },
    {functional: true}
);

export const updateSourceTargetAfterMerge = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(DatasetActions.mergeSuccess),
            map((action) => DatasetActions.setsourceandtarget({source: action.source, target: action.target}))
        );
    },
    {functional: true}
);

export const loadDatasetsAfterMergeDataset = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(DatasetActions.mergeSuccess),
            map(() => DatasetActions.load())
        );
    },
    {functional: true}
);

export const showMergeSuccess = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(DatasetActions.mergeSuccess),
            tap(() => {
                snackBar.open("Dataset merged successfully.", "", {
                    duration: 3000
                });
            })
        );
    },
    {functional: true, dispatch: false}
);

export const loadMergeJobs = createEffect(
    (actions$ = inject(Actions), datasetService = inject(DatasetService)) => {
        return actions$.pipe(
            ofType(DatasetActions.loadMergeJobs),
            switchMap(() =>
                datasetService.getAllMergeJobs().pipe(
                    map((jobs) => DatasetActions.loadMergeJobsSuccess({jobs})),
                    catchError((errorResponse: any) => of(DatasetActions.loadMergeJobsError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                )
            )
        );
    },
    {functional: true}
);

// Poll merge-jobs every 3s while any job is IN_PROGRESS/NEW. Re-evaluates after
// each successful load and tears itself down once no active jobs remain.
export const pollMergeJobsWhileActive = createEffect(
    (actions$ = inject(Actions), store = inject(Store)) => {
        return actions$.pipe(
            ofType(DatasetActions.loadMergeJobsSuccess),
            switchMap(() =>
                store.select(selectHasActiveMergeJobs).pipe(
                    switchMap(hasActive => {
                        if (!hasActive) return of();
                        return interval(3000).pipe(
                            map(() => DatasetActions.loadMergeJobs()),
                            takeUntil(actions$.pipe(ofType(DatasetActions.loadMergeJobsSuccess)))
                        );
                    })
                )
            )
        );
    },
    {functional: true}
);

export const reloadMergeJobsAfterMergeEvents = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(DatasetActions.mergeStarted, DatasetActions.mergeSuccess, DatasetActions.mergeError),
            map(() => DatasetActions.loadMergeJobs())
        );
    },
    {functional: true}
);

export const deleteDataset = createEffect(
    (actions$ = inject(Actions), datasetService = inject(DatasetService)) => {
        return actions$.pipe(
            ofType(DatasetActions.delete),
            exhaustMap((action) => {
                return datasetService.deleteDataset(action.dataset).pipe(
                    map((dataset) => DatasetActions.deleteSuccess({dataset})),
                    catchError((errorResponse) => {
                        const httpResponseError: HttpResponseError = {
                            statusCode: errorResponse.status,
                            errorMessage: ErrorHandlingUtil.getErrorMessage(errorResponse)
                        };
                        return of(DatasetActions.deleteError({httpResponseError}));
                    })
                );
            })
        );
    },
    {functional: true}
);

export const reloadDatasetsAfterDelete = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(DatasetActions.deleteSuccess),
            map(() => DatasetActions.load())
        );
    },
    {functional: true}
);

export const showDeleteSuccess = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(DatasetActions.deleteSuccess),
            tap(() => {
                snackBar.open("Dataset deleted successfully.", "", {
                    duration: 3000
                });
            })
        );
    },
    {functional: true, dispatch: false}
);

export const loadImportLogs = createEffect(
    (actions$ = inject(Actions), accelexImportService = inject(AccelexImportService)) => {
        return actions$.pipe(
            ofType(AccelexImportLogActions.loadAll),
            exhaustMap(() =>
                accelexImportService.getImportLogs().pipe(
                    map((importLogs) => AccelexImportLogActions.loadedAll({importLogs})),
                    catchError((errorResponse: any) => of(AccelexImportLogActions.loaderror({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                )
            )
        );
    },
    {functional: true}
);

// Scraper - Upload PDF (legacy — upload is now handled directly in dialog component)
export const uploadPdf = createEffect(
    (actions$ = inject(Actions), scraperService = inject(ScraperService)) => {
        return actions$.pipe(
            ofType(ScraperActions.uploadPDF),
            exhaustMap((action) => {
                return scraperService.uploadPdf(action.file).pipe(
                    map((response) => ScraperActions.uploadSuccess({jobId: response.jobId, fileName: action.fileName})),
                    catchError((errorResponse: any) => of(ScraperActions.uploadError({error: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                );
            })
        );
    },
    {functional: true}
);

// Scraper - (Re)start the shared poller after every upload success. Restarting on each file in a
// batch is harmless — it just resets the 3 s phase — and guarantees the poller is running for the
// very first file too.
export const startScraperPolling = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(ScraperActions.uploadSuccess),
            map(() => ScraperActions.startPolling())
        );
    },
    {functional: true}
);

// Switch the notification bell's poll cadence to 5 s while a scrape is in flight.
// The matching JOB_FINISHED notification untracks itself when it arrives in the panel list.
export const trackScraperJob = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(ScraperActions.uploadSuccess),
            map(({jobId}) => NotificationActions.trackJob({taskInfoId: jobId}))
        );
    },
    {functional: true}
);

// Scraper - Poll every still-pending tracked job concurrently on one shared 3 s tick (B-1327 Phase 3).
// The previous version switchMapped on a single jobId, so each new upload's poll cancelled the
// previous file's — only the last-started job in a batch was ever tracked to completion, and
// scrapeComplete/scrapeError (which also drive the error snackbar and the active-count refresh, not
// just the banner) silently fired for that one job only. Reading the pending set fresh from the
// store on every tick and polling all of it in parallel fixes that for every consumer at once.
// takeWhile stops the shared interval once nothing is pending; the next uploadSuccess restarts it.
//
// Review fixes on top of that first pass:
// - exhaustMap (not switchMap) at the top level: startPolling fires on every uploadSuccess, and a
//   batch dispatches one per file. switchMap tore the whole interval down and restarted it (losing
//   its 3 s phase and firing an immediate extra poll via startWith(0)) on every single one of them —
//   O(N^2) requests on an N-file batch. exhaustMap ignores a new startPolling while a loop is already
//   running; the loop itself re-reads the pending set fresh each tick, so a job added mid-loop is
//   still picked up on the next tick regardless.
// - an in-flight Set so a job whose status request outlives one 3 s tick isn't polled a second time
//   in parallel — without it, two overlapping responses could each resolve terminal and dispatch
//   scrapeComplete/scrapeError twice for the same job (a duplicate snackbar).
// - a concurrency cap on the per-tick mergeMap, so a large batch doesn't fire one HTTP GET per
//   pending job with no bound (mirrors the dialog's own 5-way upload concurrency, doubled since a
//   status check is far cheaper than an upload).
const POLL_CONCURRENCY = 10;

export const pollScraperJobStatus = createEffect(
    (actions$ = inject(Actions), scraperService = inject(ScraperService), store = inject(Store)) => {
        const inFlight = new Set<number>();
        return actions$.pipe(
            ofType(ScraperActions.startPolling),
            exhaustMap(() =>
                interval(3000).pipe(
                    startWith(0),
                    concatLatestFrom(() => store.select(selectPendingScraperBatchJobIds)),
                    takeWhile(([, pendingIds]) => pendingIds.length > 0),
                    mergeMap(([, pendingIds]) =>
                        from(pendingIds.filter(jobId => !inFlight.has(jobId))).pipe(
                            mergeMap((jobId) => {
                                inFlight.add(jobId);
                                return scraperService.getScrapeJobStatus(jobId).pipe(
                                    map((status) => {
                                        if (status.status === "COMPLETED") {
                                            return ScraperActions.scrapeComplete({jobId});
                                        } else if (["ERROR", "TIMEOUT", "CANCELLED"].includes(status.status)) {
                                            return ScraperActions.scrapeError({jobId, error: status.errorMessage || "Extraction failed"});
                                        }
                                        return null;
                                    }),
                                    catchError((errorResponse: any) => of(ScraperActions.scrapeError({jobId, error: ErrorHandlingUtil.getErrorMessage(errorResponse)}))),
                                    finalize(() => inFlight.delete(jobId))
                                );
                            }, POLL_CONCURRENCY)
                        )
                    ),
                    filter((action): action is ReturnType<typeof ScraperActions.scrapeComplete> | ReturnType<typeof ScraperActions.scrapeError> => action !== null)
                )
            )
        );
    },
    {functional: true}
);

// Scraper - Load result after scrape complete or on page load
export const loadScraperResult = createEffect(
    (actions$ = inject(Actions), scraperService = inject(ScraperService)) => {
        return actions$.pipe(
            ofType(ScraperActions.loadResult),
            exhaustMap((action) =>
                scraperService.getScrapeResult(action.jobId).pipe(
                    map((result) => ScraperActions.resultLoaded({result})),
                    catchError((errorResponse: any) => of(ScraperActions.resultError({error: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                )
            )
        );
    },
    {functional: true}
);

// Scraper - Page geometry, loaded next to the result so the viewer can lay the document out
export const loadScraperPages = createEffect(
    (actions$ = inject(Actions), scraperService = inject(ScraperService)) => {
        return actions$.pipe(
            ofType(ScraperActions.loadPages),
            // switchMap, not exhaustMap: opening another job while this one is still loading must
            // show the new document's pages, not silently keep the old request.
            switchMap((action) =>
                scraperService.getPageGeometry(action.jobId).pipe(
                    map((pages) => ScraperActions.loadPagesSuccess({pages})),
                    catchError((errorResponse: any) => of(ScraperActions.loadPagesError({error: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                )
            )
        );
    },
    {functional: true}
);

// Scraper - Word layer, loaded only once search or "Add from PDF" needs it
export const loadScraperWords = createEffect(
    (actions$ = inject(Actions), scraperService = inject(ScraperService)) => {
        return actions$.pipe(
            ofType(ScraperActions.loadWords),
            switchMap((action) =>
                scraperService.getWords(action.jobId).pipe(
                    // Abandoned as soon as another document is opened. Without this, a word layer
                    // still in flight for the previous job arrives after the reducer has cleared the
                    // slice and is stored as the new document's — search would then highlight at
                    // coordinates from the wrong PDF, "Add from PDF" would commit its text onto the
                    // new job's records, and the slice reading LOADED means the new document never
                    // asks for its own.
                    takeUntil(actions$.pipe(ofType(ScraperActions.loadPages))),
                    map((pages) => ScraperActions.loadWordsSuccess({pages})),
                    catchError((errorResponse: any) => of(ScraperActions.loadWordsError({error: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                )
            )
        );
    },
    {functional: true}
);

// Scraper - Approve import
export const approveScraperImport = createEffect(
    (actions$ = inject(Actions), scraperService = inject(ScraperService)) => {
        return actions$.pipe(
            ofType(ScraperActions.approveImport),
            exhaustMap((action) =>
                scraperService.approveScrapeImport(action.jobId, action.dataset, action.refDocument).pipe(
                    map((response) => ScraperActions.importSuccess({importResponse: response})),
                    catchError((errorResponse: any) => of(ScraperActions.importError({error: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                )
            )
        );
    },
    {functional: true}
);

// Scraper - Reload datasets after import success
export const reloadDatasetsAfterScraperImport = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(ScraperActions.importSuccess),
            map(() => DatasetActions.load())
        );
    },
    {functional: true}
);

// Scraper - Show snackbar after scrape complete with link to import overview
export const showScraperComplete = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar), router = inject(Router), store = inject(Store)) => {
        return actions$.pipe(
            ofType(ScraperActions.scrapeComplete),
            // Skip for a multi-file batch (B-1327 Phase 3): scrapeComplete now fires once per file
            // instead of once for whichever job survived the old switchMap, so unconditionally
            // showing this would stack a 10 s snackbar per file. The banner + notification bell
            // already cover per-file completion for a batch; keep this snackbar for the single-file
            // upload flow it was designed for.
            concatLatestFrom(() => store.select(selectScraperBatchJobs)),
            filter(([, jobs]) => jobs.length <= 1),
            tap(() => {
                const ref = snackBar.open("PDF data extracted successfully.", "View", {duration: 10000});
                ref.onAction().subscribe(() => router.navigate(["/import"]));
            })
        );
    },
    {functional: true, dispatch: false}
);

// Scraper - Show import success snackbar (with warning when fund size is missing)
export const showScraperImportSuccess = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar), router = inject(Router), store = inject(Store)) => {
        return actions$.pipe(
            ofType(ScraperActions.importSuccess),
            tap(({importResponse}) => {
                const missingFundSize = importResponse.warnings?.includes(ImportWarning.MISSING_FUND_SIZE);
                if (missingFundSize && importResponse.fundId != null) {
                    const fundId = importResponse.fundId;
                    const ref = snackBar.open(
                        "Imported. Fund size is missing — please update it.",
                        "Update fund size",
                        {duration: 10000, panelClass: "snackbar-warning"}
                    );
                    ref.onAction().pipe(take(1)).subscribe(() => {
                        navigateToImportedFund(router, store, fundId, importResponse.dataset);
                    });
                } else if (importResponse.fundId != null) {
                    snackBar.open("Data imported successfully.", "", {duration: 3000});
                }
                // CAS imports (fundId == null) are handled by reviewCapitalAccountsAfterCasImport below.
            })
        );
    },
    {functional: true, dispatch: false}
);

// Scraper - After approving a CAS, land on the capital-account review page with that dataset loaded
export const reviewCapitalAccountsAfterCasImport = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar), router = inject(Router), store = inject(Store)) => {
        return actions$.pipe(
            ofType(ScraperActions.importSuccess),
            filter(({importResponse}) => isCapitalAccountImport(importResponse)),
            tap(({importResponse}) => {
                const dataset = importResponse.dataset as string;
                store.dispatch(DatasetActions.setsourceandtarget({source: dataset, target: undefined}));
                store.dispatch(CapitalAccountReviewActions.loadForDataset({dataset}));
                router.navigate(["/import/capital-accounts"]);
                snackBar.open("Capital accounts staged — assign or defer each below.", "", {duration: 4000});
            })
        );
    },
    {functional: true, dispatch: false}
);

// Scraper - Show scrape error snackbar
export const showScraperError = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar), store = inject(Store)) => {
        return actions$.pipe(
            ofType(ScraperActions.scrapeError),
            // Same gate as showScraperComplete, for the same reason (review fix): scrapeError now
            // fires once per failing file instead of once per polling session, so an ungated batch
            // with several failures would stack an 8 s red toast per failure. The banner's inline
            // "· K failed" count and the jobs table's own ERROR rows already surface batch failures.
            concatLatestFrom(() => store.select(selectScraperBatchJobs)),
            filter(([, jobs]) => jobs.length <= 1),
            tap(([action]) => {
                snackBar.open("PDF extraction failed: " + action.error, "Dismiss", {duration: 8000});
            })
        );
    },
    {functional: true, dispatch: false}
);

// Scraper - Show batch upload success snackbar
export const showBatchUploadSuccess = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(ScraperActions.batchUploadSuccess),
            tap((action) => {
                snackBar.open(`Started extracting ${action.fileCount} PDFs.`, "", {duration: 5000});
            })
        );
    },
    {functional: true, dispatch: false}
);

// Scraper - Show batch upload error snackbar
export const showBatchUploadError = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(ScraperActions.batchUploadError),
            tap((action) => {
                snackBar.open("Batch upload failed: " + action.error, "Dismiss", {duration: 8000});
            })
        );
    },
    {functional: true, dispatch: false}
);

// Scraper - Show upload error snackbar
export const showScraperUploadError = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(ScraperActions.uploadError),
            tap((action) => {
                snackBar.open("PDF upload failed: " + action.error, "Dismiss", {duration: 8000});
            })
        );
    },
    {functional: true, dispatch: false}
);

// Scraper - Navigate to import overview after approve success
export const navigateAfterScraperImport = createEffect(
    (actions$ = inject(Actions), router = inject(Router)) => {
        return actions$.pipe(
            ofType(ScraperActions.importSuccess),
            // CAS imports land on the capital-account review page instead — see
            // reviewCapitalAccountsAfterCasImport. The two effects must stay mutually exclusive.
            filter(({importResponse}) => !isCapitalAccountImport(importResponse)),
            exhaustMap(() => router.navigate(["/import"]))
        );
    },
    {dispatch: false, functional: true}
);

// Scraper - Search jobs from server (reads params from store, resolves myExtractionsOnly to userName)
export const searchScraperJobs = createEffect(
    (actions$ = inject(Actions), scraperService = inject(ScraperService), store = inject(Store), authService = inject(AuthenticationService)) => {
        return actions$.pipe(
            ofType(ScraperActions.searchJobs),
            concatLatestFrom(() => store.select(selectScraperJobsParams)),
            switchMap(([, params]) => {
                const userName = params.myExtractionsOnly ? authService.currentUser?.userName : undefined;
                const requestParams = {
                    search: params.search || undefined,
                    userName,
                    page: params.page,
                    size: params.size,
                    sort: params.sort
                };
                return scraperService.getScraperJobs(requestParams).pipe(
                    map((response) => ScraperActions.searchJobsSuccess({
                        jobs: response.content ?? [],
                        totalElements: response.totalElements ?? 0
                    })),
                    catchError((errorResponse: any) => of(ScraperActions.searchJobsError({error: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                );
            })
        );
    },
    {functional: true}
);

// Scraper - Trigger search when params change
export const triggerSearchOnParamsChange = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(ScraperActions.changePage, ScraperActions.changeSort, ScraperActions.changeSearch, ScraperActions.changeMyExtractions),
            map(() => ScraperActions.searchJobs())
        );
    },
    {functional: true}
);

// Scraper - Refresh jobs after upload success
export const refreshJobsAfterUpload = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(ScraperActions.uploadSuccess),
            map(() => ScraperActions.searchJobs())
        );
    },
    {functional: true}
);

// Scraper - Load active job count (drives upload-dialog 60-file cap)
export const loadActiveScraperJobCount = createEffect(
    (actions$ = inject(Actions), scraperService = inject(ScraperService)) => {
        return actions$.pipe(
            ofType(ScraperActions.loadActiveJobCount),
            switchMap(() =>
                scraperService.getScraperJobs({activeOnly: true, page: 0, size: 1}).pipe(
                    map((response) => ScraperActions.loadActiveJobCountSuccess({count: response.totalElements ?? 0})),
                    catchError((errorResponse: any) => of(ScraperActions.loadActiveJobCountError({error: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                )
            )
        );
    },
    {functional: true}
);

// Scraper - Load MY active job count (progress-banner fallback after a reload, B-1327 Phase 3).
// Distinct from loadActiveScraperJobCount, which must stay instance-wide for the 60-file cap.
export const loadMyActiveScraperJobCount = createEffect(
    (actions$ = inject(Actions), scraperService = inject(ScraperService), authService = inject(AuthenticationService)) => {
        return actions$.pipe(
            ofType(ScraperActions.loadMyActiveJobCount),
            switchMap(() =>
                scraperService.getScraperJobs({activeOnly: true, userName: authService.currentUser?.userName, page: 0, size: 1}).pipe(
                    map((response) => ScraperActions.loadMyActiveJobCountSuccess({count: response.totalElements ?? 0})),
                    catchError((errorResponse: any) => of(ScraperActions.loadMyActiveJobCountError({error: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                )
            )
        );
    },
    {functional: true}
);

// Scraper - Keep polling MY active count every 5 s while it's non-zero, so the banner's post-reload
// fallback (no per-file breakdown available) still counts down as jobs finish. Mirrors
// autoRefreshProcessingJobs's cadence/shape.
export const autoRefreshMyActiveScraperJobCount = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(ScraperActions.loadMyActiveJobCountSuccess),
            switchMap((action) => {
                if (action.count === 0) return EMPTY;
                return interval(5000).pipe(
                    map(() => ScraperActions.loadMyActiveJobCount()),
                    takeUntil(actions$.pipe(ofType(ScraperActions.loadMyActiveJobCountSuccess)))
                );
            })
        );
    },
    {functional: true}
);

// Scraper - Refresh active job count after every upload-lifecycle action so the dialog stays accurate
export const refreshActiveCountOnUploadLifecycle = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(
                ScraperActions.uploadSuccess,
                ScraperActions.uploadError,
                ScraperActions.batchUploadSuccess,
                ScraperActions.batchUploadError,
                ScraperActions.scrapeComplete,
                ScraperActions.scrapeError
            ),
            map(() => ScraperActions.loadActiveJobCount())
        );
    },
    {functional: true}
);

// Scraper - Same upload-lifecycle actions, refreshing MY count (progress banner) alongside the
// instance-wide one above.
export const refreshMyActiveCountOnUploadLifecycle = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(
                ScraperActions.uploadSuccess,
                ScraperActions.uploadError,
                ScraperActions.batchUploadSuccess,
                ScraperActions.batchUploadError,
                ScraperActions.scrapeComplete,
                ScraperActions.scrapeError
            ),
            map(() => ScraperActions.loadMyActiveJobCount())
        );
    },
    {functional: true}
);

// Scraper - Refresh jobs after scrape complete
export const refreshJobsAfterScrape = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(ScraperActions.scrapeComplete),
            map(() => ScraperActions.searchJobs())
        );
    },
    {functional: true}
);

// Scraper - Refresh jobs after import success
export const refreshJobsAfterImport = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(ScraperActions.importSuccess),
            map(() => ScraperActions.searchJobs())
        );
    },
    {functional: true}
);

// Scraper - Cancel a queued/processing job
export const cancelScrapeJob = createEffect(
    (actions$ = inject(Actions), scraperService = inject(ScraperService)) => {
        return actions$.pipe(
            ofType(ScraperActions.cancelJob),
            mergeMap(({jobId}) =>
                scraperService.cancelJob(jobId).pipe(
                    map(() => ScraperActions.cancelJobSuccess({jobId})),
                    catchError((errorResponse: any) => of(ScraperActions.cancelJobError({jobId, error: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                )
            )
        );
    },
    {functional: true}
);

// Scraper - Refresh jobs + active count after a successful cancel so CANCELLING shows up immediately
export const refreshJobsAfterCancel = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(ScraperActions.cancelJobSuccess),
            mergeMap(() => [ScraperActions.searchJobs(), ScraperActions.loadActiveJobCount()])
        );
    },
    {functional: true}
);

// Scraper - Apply provenance edit (EDIT/DELETE/ADD/RESET)
export const applyProvenanceEdit = createEffect(
    (actions$ = inject(Actions), scraperService = inject(ScraperService)) => {
        return actions$.pipe(
            ofType(ScraperActions.applyProvenanceEdit),
            // concatMap (not mergeMap): serialize edits so two requests are never in flight at
            // once — concurrent requests carrying the same version could otherwise both pass the
            // server's version check and silently clobber each other. concatMap over exhaustMap so
            // a second distinct edit isn't dropped; if it now carries a stale version the server
            // returns 409 and refetchOnEditConflict refreshes the view.
            concatMap((action) =>
                scraperService.editProvenance(action.jobId, action.version, action.change).pipe(
                    map((result) => ScraperActions.applyProvenanceEditSuccess({result})),
                    catchError((errorResponse: any) => {
                        const conflict = errorResponse?.status === 409;
                        return of(ScraperActions.applyProvenanceEditError({
                            error: ErrorHandlingUtil.getErrorMessage(errorResponse),
                            conflict
                        }));
                    })
                )
            )
        );
    },
    {functional: true}
);

// Scraper - Refetch result when an edit is rejected due to version conflict
export const refetchOnEditConflict = createEffect(
    (actions$ = inject(Actions), store = inject(Store), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(ScraperActions.applyProvenanceEditError),
            tap((action) => {
                if (action.conflict) {
                    snackBar.open("Another edit happened in the meantime. Refreshing your view.", "Dismiss", {duration: 5000});
                }
            }),
            mergeMap((action) =>
                action.conflict
                    ? store.select(selectScraperJobId).pipe(
                        take(1),
                        mergeMap((jobId) => jobId != null ? of(ScraperActions.loadResult({jobId})) : of())
                    )
                    : of()
            )
        );
    },
    {functional: true}
);

// Scraper - Reset all edits
export const resetAllEdits = createEffect(
    (actions$ = inject(Actions), scraperService = inject(ScraperService)) => {
        return actions$.pipe(
            ofType(ScraperActions.resetAllEdits),
            exhaustMap((action) =>
                scraperService.resetAllEdits(action.jobId).pipe(
                    map((result) => ScraperActions.resetAllEditsSuccess({result})),
                    catchError((errorResponse: any) => of(ScraperActions.resetAllEditsError({error: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                )
            )
        );
    },
    {functional: true}
);

// Scraper - Load edit log
export const loadEditLog = createEffect(
    (actions$ = inject(Actions), scraperService = inject(ScraperService)) => {
        return actions$.pipe(
            ofType(ScraperActions.loadEditLog),
            switchMap((action) =>
                scraperService.getEditLog(action.jobId, action.recordId).pipe(
                    map((entries) => ScraperActions.loadEditLogSuccess({entries})),
                    catchError((errorResponse: any) => of(ScraperActions.loadEditLogError({error: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                )
            )
        );
    },
    {functional: true}
);

// Scraper - Keep the edit log fresh after a change once it's already been loaded.
// The log is otherwise loaded lazily only when the Edit history tab is opened (loadEditLog).
// ADD and RESET_ALL can be triggered while that tab is already open (they live on the toolbar,
// not the provenance table), so without this the log would show stale entries until the user
// leaves and re-opens the tab. Gated on status !== "INIT" so users who never open the tab don't
// incur an extra request per edit.
export const reloadEditLogAfterChange = createEffect(
    (actions$ = inject(Actions), store = inject(Store)) => {
        return actions$.pipe(
            ofType(ScraperActions.applyProvenanceEditSuccess, ScraperActions.resetAllEditsSuccess),
            concatLatestFrom(() => [store.select(selectScraperJobId), store.select(selectScraperEditLog)]),
            mergeMap(([, jobId, editLog]) =>
                jobId != null && editLog.status !== "INIT"
                    ? of(ScraperActions.loadEditLog({jobId}))
                    : of()
            )
        );
    },
    {functional: true}
);

// Scraper - Load field schema (canonical editable fields)
export const loadFieldSchema = createEffect(
    (actions$ = inject(Actions), scraperService = inject(ScraperService)) => {
        return actions$.pipe(
            ofType(ScraperActions.loadFieldSchema),
            exhaustMap(() =>
                scraperService.getFieldSchema().pipe(
                    map((fields) => ScraperActions.loadFieldSchemaSuccess({fields})),
                    catchError((errorResponse: any) => of(ScraperActions.loadFieldSchemaError({error: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                )
            )
        );
    },
    {functional: true}
);

// Scraper - Show snackbar when an edit succeeds
export const showEditSuccess = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(ScraperActions.applyProvenanceEditSuccess),
            tap(() => snackBar.open("Edit saved.", "", {duration: 2000}))
        );
    },
    {functional: true, dispatch: false}
);

// Scraper - Show snackbar when reset succeeds
export const showResetSuccess = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(ScraperActions.resetAllEditsSuccess),
            tap(() => snackBar.open("All edits cleared.", "", {duration: 3000}))
        );
    },
    {functional: true, dispatch: false}
);

// Scraper - Auto-refresh when there are processing jobs
export const autoRefreshProcessingJobs = createEffect(
    (actions$ = inject(Actions), store = inject(Store)) => {
        return actions$.pipe(
            ofType(ScraperActions.searchJobsSuccess),
            switchMap(() =>
                store.select(selectHasProcessingJobs).pipe(
                    switchMap(hasProcessing => {
                        if (!hasProcessing) return of();
                        return interval(5000).pipe(
                            map(() => ScraperActions.searchJobs()),
                            takeUntil(actions$.pipe(ofType(ScraperActions.searchJobsSuccess)))
                        );
                    })
                )
            )
        );
    },
    {functional: true}
);

// --- Forecast Import (Extract & Forecast Excel bulk import) ---
// Note: verify/commit HTTP is performed in the wizard component (the File cannot live in a
// serializable action/store), which dispatches the verifySuccess/commitSuccess lifecycle actions.

// A committed import mints a new dataset; refresh the overview's dataset list so it shows up.
export const reloadDatasetsAfterForecastCommit = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(ForecastImportActions.commitSuccess),
            map(() => DatasetActions.load())
        );
    },
    {functional: true}
);

// --- Forecast Review (re-open → match → field-selective merge to master) ---

export const loadForecastImportList = createEffect(
    (actions$ = inject(Actions), service = inject(ForecastImportService)) => {
        return actions$.pipe(
            ofType(ForecastReviewActions.loadList),
            switchMap(() =>
                service.listImports().pipe(
                    map((imports) => ForecastReviewActions.loadListSuccess({imports})),
                    catchError((err) => of(ForecastReviewActions.loadListError({error: ErrorHandlingUtil.getErrorMessage(err)})))
                )
            )
        );
    },
    {functional: true}
);

export const loadForecastReview = createEffect(
    (actions$ = inject(Actions), service = inject(ForecastImportService)) => {
        return actions$.pipe(
            ofType(ForecastReviewActions.load),
            switchMap(({importId}) =>
                service.getImport(importId).pipe(
                    map((detail) => ForecastReviewActions.loadSuccess({detail})),
                    catchError((err) => of(ForecastReviewActions.loadError({error: ErrorHandlingUtil.getErrorMessage(err)})))
                )
            )
        );
    },
    {functional: true}
);

// Persisting a match (or clearing it for Create New) reuses the existing fund merge-target endpoint;
// it auto-links fund reports by report date. Reload the re-open view afterwards to reflect the change.
export const assignForecastMatch = createEffect(
    (actions$ = inject(Actions), fundApi = inject(FundApiService)) => {
        return actions$.pipe(
            ofType(ForecastReviewActions.assignMatch),
            mergeMap(({importId, fundId, masterFundId, dataset}) =>
                fundApi.setMergeTargetFund(fundId, undefined, masterFundId, dataset).pipe(
                    map(() => ForecastReviewActions.load({importId})),
                    catchError((err) => of(ForecastReviewActions.matchUpdateError({error: ErrorHandlingUtil.getErrorMessage(err)})))
                )
            )
        );
    },
    {functional: true}
);

export const flagForecastCreateNew = createEffect(
    (actions$ = inject(Actions), fundApi = inject(FundApiService)) => {
        return actions$.pipe(
            ofType(ForecastReviewActions.flagCreateNew),
            mergeMap(({importId, fundId, dataset}) =>
                fundApi.setMergeTargetFund(fundId, undefined, undefined, dataset).pipe(
                    map(() => ForecastReviewActions.load({importId})),
                    catchError((err) => of(ForecastReviewActions.matchUpdateError({error: ErrorHandlingUtil.getErrorMessage(err)})))
                )
            )
        );
    },
    {functional: true}
);

// Bulk "Create New for all unmatched": clear any merge target on every still-unmatched staged fund
// (mirrors per-row flagCreateNew which clears via setMergeTargetFund). Each request is wrapped in
// toBulkResult so a single failure no longer aborts the batch: we still reload the view and report
// partial success, instead of leaving already-persisted rows out of sync with the UI.
export const flagAllUnmatchedForecastCreateNew = createEffect(
    (actions$ = inject(Actions), fundApi = inject(FundApiService)) => {
        return actions$.pipe(
            ofType(ForecastReviewActions.flagAllUnmatchedCreateNew),
            mergeMap(({importId, fundIds, dataset}) => {
                if (fundIds.length === 0) {
                    return of(ForecastReviewActions.load({importId}));
                }
                return forkJoin(fundIds.map((fundId) => toBulkResult(fundApi.setMergeTargetFund(fundId, undefined, undefined, dataset)))).pipe(
                    mergeMap((results) => {
                        const {count, failed, firstError} = summarizeBulkResults(results);
                        return count === 0
                            ? [ForecastReviewActions.matchUpdateError({error: firstError ?? "Funds could not be flagged for Create New."})]
                            : [ForecastReviewActions.bulkMatchUpdateNotice({count, failed}), ForecastReviewActions.load({importId})];
                    })
                );
            })
        );
    },
    {functional: true}
);

export const acceptAllSuggestedForecastMatches = createEffect(
    (actions$ = inject(Actions), fundApi = inject(FundApiService)) => {
        return actions$.pipe(
            ofType(ForecastReviewActions.acceptAllSuggested),
            mergeMap(({importId, matches, dataset}) => {
                if (matches.length === 0) {
                    return of(ForecastReviewActions.load({importId}));
                }
                return forkJoin(matches.map((match) => toBulkResult(fundApi.setMergeTargetFund(match.fundId, undefined, match.masterFundId, dataset)))).pipe(
                    mergeMap((results) => {
                        const {count, failed, firstError} = summarizeBulkResults(results);
                        return count === 0
                            ? [ForecastReviewActions.matchUpdateError({error: firstError ?? "Suggested matches could not be accepted."})]
                            : [ForecastReviewActions.bulkMatchUpdateNotice({count, failed}), ForecastReviewActions.load({importId})];
                    })
                );
            })
        );
    },
    {functional: true}
);

// Partial-success snackbar for the two bulk match operations above.
export const showBulkMatchUpdateNotice = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(ForecastReviewActions.bulkMatchUpdateNotice),
            tap(({count, failed}) => {
                const noun = count === 1 ? "fund" : "funds";
                const failedSuffix = failed > 0 ? ` ${failed} failed.` : "";
                snackBar.open(`${count} ${noun} updated.${failedSuffix}`, "", {duration: 3000});
            })
        );
    },
    {functional: true, dispatch: false}
);

// Create a deal inline for the assignment step via DealService.create (NOT DealActions.create, which
// auto-navigates to the deal page via an effect — we must stay on the wizard until the final merge).
export const createDealForImport = createEffect(
    (actions$ = inject(Actions), dealService = inject(DealService)) => {
        return actions$.pipe(
            ofType(ForecastReviewActions.createDealForImport),
            exhaustMap(({deal}) =>
                dealService.create(deal).pipe(
                    map((created) => ForecastReviewActions.createDealForImportSuccess({deal: created})),
                    catchError((err) => of(ForecastReviewActions.createDealForImportError({error: ErrorHandlingUtil.getErrorMessage(err)})))
                )
            )
        );
    },
    {functional: true}
);

// Start the async merge job; the merge itself (merge + deal assignment + NAV-multiple goal-seek
// pre-population) runs server-side and is polled below. exhaustMap ignores a double-submit while a
// start is in flight.
export const mergeForecastImport = createEffect(
    (actions$ = inject(Actions), service = inject(ForecastImportService)) => {
        return actions$.pipe(
            ofType(ForecastReviewActions.merge),
            exhaustMap(({importId, dealAssignment}) =>
                service.merge(importId, dealAssignment ? {dealAssignment} : undefined).pipe(
                    map(({taskInfoId}) => ForecastReviewActions.mergeStarted({taskInfoId})),
                    // A failed start is indeterminate — a retry hitting the already-merged 409, or a network
                    // blip after the server accepted the job — so it is recoverable by reloading the detail.
                    catchError((err) => of(ForecastReviewActions.mergeError({error: ErrorHandlingUtil.getErrorMessage(err), recoverable: true})))
                )
            )
        );
    },
    {functional: true}
);

// Poll the merge job every 2 s (clone of the Accelex pollingJobStatus pattern). The job flips to
// COMPLETED only once the merge — including NAV-multiple goal-seek pre-population — has finished, so
// mergeSuccess (which advances the wizard to the seeded NAV-multiples step) never fires early.
// switchMap restarts polling cleanly if mergeStarted re-fires (e.g. resume on page re-entry).
export const pollForecastMergeJob = createEffect(
    (actions$ = inject(Actions), service = inject(ForecastImportService)) => {
        return actions$.pipe(
            ofType(ForecastReviewActions.mergeStarted),
            switchMap(({taskInfoId}) =>
                interval(MERGE_POLL_INTERVAL_MS).pipe(
                    startWith(0),
                    switchMap(() =>
                        service.getMergeJobStatus(taskInfoId).pipe(
                            map((status): MergePollEvent => ({kind: "status", status})),
                            catchError((err) => of<MergePollEvent>({kind: "requestFailed", error: ErrorHandlingUtil.getErrorMessage(err)}))
                        )
                    ),
                    scan(reduceMergePoll, INITIAL_MERGE_POLL_STATE),
                    map((acc) => acc.action),
                    takeUntil(actions$.pipe(ofType(ForecastReviewActions.mergeSuccess, ForecastReviewActions.mergeError)))
                )
            )
        );
    },
    {functional: true}
);

// Re-entry: fetch the persisted merge outcome of an already-merged import once (no polling, no tracking)
// so the review page can recover the deal id, created valuation ids and skipped funds and re-open the
// top-down forecast review. On a COMPLETED job we replay the result; on a job that ended in a problem
// (ERROR/TIMEOUT/CANCELLED, or COMPLETED with no persisted result) we surface its error message via
// mergeError so the read-only merged view can explain what went wrong instead of showing nothing.
// A transport failure alone is swallowed — the merged view then renders the plain per-fund results.
export const loadForecastMergeResult = createEffect(
    (actions$ = inject(Actions), service = inject(ForecastImportService)) => {
        return actions$.pipe(
            ofType(ForecastReviewActions.loadMergeResult),
            switchMap(({taskInfoId}) =>
                service.getMergeJobStatus(taskInfoId).pipe(
                    mergeMap((status) => {
                        if (status.status === "COMPLETED" && status.result) {
                            return of(ForecastReviewActions.mergeResultLoaded({result: status.result}));
                        }
                        // A terminal failure (or a COMPLETED job whose result could not be persisted) is
                        // surfaced so the read-only merged view can explain it; a still-running status emits
                        // nothing (shouldn't happen for a MERGED import, but never shows a false error).
                        const finishedWithProblem = ["ERROR", "TIMEOUT", "CANCELLED", "COMPLETED"].includes(status.status);
                        return finishedWithProblem
                            ? of(ForecastReviewActions.mergeError({
                                error: status.errorMessage
                                    ?? "The merge did not finish successfully. Some funds may not have been assigned to the deal."
                            }))
                            : EMPTY;
                    }),
                    catchError(() => EMPTY)
                )
            )
        );
    },
    {functional: true}
);

// Speed up the notification bell's poll cadence while the merge runs; the JOB_FINISHED notification
// untracks itself on arrival (mirrors trackMergeJob for dataset merges).
export const trackForecastMergeJob = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(ForecastReviewActions.mergeStarted),
            map(({taskInfoId}) => NotificationActions.trackJob({taskInfoId}))
        );
    },
    {functional: true}
);

// On a successful merge, show a summary snackbar. With a deal, the review page advances to its in-wizard
// NAV-multiples step (no navigation here); without a deal, return to the import overview.
export const navigateAfterForecastMerge = createEffect(
    (actions$ = inject(Actions), router = inject(Router), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(ForecastReviewActions.mergeSuccess),
            tap(({result}) => {
                const merged = result.mergedFundIds.length;
                const created = result.createdFundIds.length;
                const skipped = result.assignmentFailures?.length ?? 0;
                const skippedNote = skipped > 0 ? `; ${skipped} fund${skipped === 1 ? "" : "s"} skipped (could not forecast)` : "";
                snackBar.open(`Merged ${merged} fund${merged === 1 ? "" : "s"}, created ${created} new in master${skippedNote}`, "Dismiss", {duration: 6000});
                if (result.dealId == null) {
                    router.navigate(["/import"]);
                }
            })
        );
    },
    {functional: true, dispatch: false}
);

/**
 * How many exports may be in flight at once.
 *
 * Each one is a full server-side render, so an unbounded mergeMap let a user clicking down twenty
 * history rows ask for twenty simultaneously. Queuing costs the user nothing — the downloads still all
 * arrive, and none is cancelled.
 */
const MAX_CONCURRENT_EXPORTS = 3;

/**
 * A name to save under when the server sent no Content-Disposition. Only a fallback — the server's own
 * name is what carries the audit context.
 */
const fallbackExportFileName = (request: ScrapeExportRequest): string =>
    request.jobIds.length === 1
        ? `extraction-job-${request.jobIds[0]}.${request.format}`
        : `extractions_${request.jobIds.length}-jobs.zip`;

// Scraper - Download a finished job's extraction.
//
// The download itself happens here rather than in the component for the same reason navigation and
// snackbars do: it is a browser side effect with no place in the store. Nothing about the file is
// kept — the outcome is a saved file, not state anything renders.
export const exportScrapeResult = createEffect(
    (actions$ = inject(Actions), scraperService = inject(ScraperService)) => {
        return actions$.pipe(
            ofType(ScraperActions.exportResult),
            // mergeMap, not switchMap: exporting several jobs in a row must not cancel the earlier
            // downloads, which is exactly what a user clicking down a list of rows will do. Bounded,
            // though — each export is a full server-side render, and clicking down twenty rows should
            // queue them rather than ask for twenty at once.
            mergeMap((action) =>
                scraperService.exportScrapeResult(action.request).pipe(
                    map((response) => {
                        const fileName = DownloadUtil.fileNameFromContentDisposition(
                            response.headers.get("content-disposition")
                        ) ?? fallbackExportFileName(action.request);
                        // A 200 with no body would silently "succeed" into a 0-byte file, so treat it
                        // as the failure it is rather than saving something unopenable.
                        if (!response.body) {
                            return ScraperActions.exportResultError({
                                jobIds: action.request.jobIds, error: "The export came back empty."
                            });
                        }
                        DownloadUtil.startDownload(response.body, fileName);
                        return ScraperActions.exportResultSuccess({jobIds: action.request.jobIds, fileName});
                    }),
                    // getErrorMessage$, not getErrorMessage: this request is made with
                    // responseType "blob", so a 4xx body arrives as an unparsed Blob and the sync
                    // reader would show Angular's "Http failure response ...: 400 OK" instead of the
                    // server's own reason ("Job 4811 is PROCESSING; only a finished extraction ...").
                    catchError((errorResponse: any) => ErrorHandlingUtil.getErrorMessage$(errorResponse).pipe(
                        map(error => ScraperActions.exportResultError({jobIds: action.request.jobIds, error}))
                    ))
                ),
                MAX_CONCURRENT_EXPORTS
            )
        );
    },
    {functional: true}
);

export const showExportScrapeResultSuccess = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(ScraperActions.exportResultSuccess),
            tap(({fileName}) => snackBar.open(`Downloaded ${fileName}`, "", {duration: 4000}))
        );
    },
    {functional: true, dispatch: false}
);

export const showExportScrapeResultError = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(ScraperActions.exportResultError),
            tap(({error}) => snackBar.open("Export failed: " + error, "Dismiss", {duration: 6000}))
        );
    },
    {functional: true, dispatch: false}
);
